import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { firewallService, FirewallVorlage } from '../services/firewallService';
import { useNotification } from '../hooks/NotificationContext';
import ConfirmDialog from '../components/common/ConfirmDialog';

type VorlageForm = Omit<FirewallVorlage, 'id'>;

const emptyForm: VorlageForm = {
  button_label_de: '',
  button_label_en: '',
  text_de: '',
  text_en: '',
  sort_order: 0,
  aktiv: true,
};

const formOf = (row: FirewallVorlage): VorlageForm => ({
  button_label_de: row.button_label_de,
  button_label_en: row.button_label_en,
  text_de: row.text_de,
  text_en: row.text_en,
  sort_order: row.sort_order,
  aktiv: row.aktiv,
});

const Leer: React.FC = () => (
  <Typography component="span" variant="caption" color="text.disabled">– (Fallback auf andere Sprache)</Typography>
);

const LanguageColumn: React.FC<{
  sprache: 'de' | 'en';
  form: VorlageForm;
  onChange: (patch: Partial<VorlageForm>) => void;
}> = ({ sprache, form, onChange }) => {
  const labelKey = sprache === 'de' ? 'button_label_de' : 'button_label_en';
  const textKey = sprache === 'de' ? 'text_de' : 'text_en';
  return (
    <Box sx={{ flex: 1, minWidth: 0 }} data-testid={`firewall-vorlage-${sprache}`}>
      <Typography variant="overline" sx={{ letterSpacing: 1 }}>{sprache === 'de' ? 'Deutsch' : 'English'}</Typography>
      <TextField
        label={sprache === 'de' ? 'Button (DE)' : 'Button (EN)'}
        value={form[labelKey]}
        onChange={(event) => onChange({ [labelKey]: event.target.value } as Partial<VorlageForm>)}
        fullWidth
        margin="dense"
      />
      <TextField
        label={sprache === 'de' ? 'Text (DE)' : 'Text (EN)'}
        value={form[textKey]}
        onChange={(event) => onChange({ [textKey]: event.target.value } as Partial<VorlageForm>)}
        fullWidth
        margin="dense"
        multiline
        minRows={3}
      />
    </Box>
  );
};

const FirewallVorlagenPanel: React.FC = () => {
  const { showSuccess, showError } = useNotification();
  const [rows, setRows] = useState<FirewallVorlage[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<FirewallVorlage | null>(null);
  const [form, setForm] = useState<VorlageForm>(emptyForm);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await firewallService.listVorlagen(true));
    } catch {
      showError('Vorlagen konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    void load();
  }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm({ ...emptyForm, sort_order: rows.length });
    setDialogOpen(true);
  };

  const openEdit = (row: FirewallVorlage) => {
    setEditing(row);
    setForm(formOf(row));
    setDialogOpen(true);
  };

  const patch = (changes: Partial<VorlageForm>) => setForm((prev) => ({ ...prev, ...changes }));

  const hasLabel = form.button_label_de.trim().length > 0 || form.button_label_en.trim().length > 0;
  const hasText = form.text_de.trim().length > 0 || form.text_en.trim().length > 0;

  const save = async () => {
    try {
      if (editing) {
        await firewallService.updateVorlage(editing.id, form);
      } else {
        await firewallService.createVorlage(form);
      }
      setDialogOpen(false);
      showSuccess('Vorlage gespeichert');
      await load();
    } catch {
      showError('Vorlage konnte nicht gespeichert werden.');
    }
  };

  const move = async (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= rows.length) return;
    const current = rows[index];
    const other = rows[target];
    try {
      await firewallService.updateVorlage(current.id, { ...formOf(current), sort_order: other.sort_order });
      await firewallService.updateVorlage(other.id, { ...formOf(other), sort_order: current.sort_order });
      await load();
    } catch {
      showError('Reihenfolge konnte nicht geändert werden.');
    }
  };

  const remove = async () => {
    if (deleteId == null) return;
    try {
      await firewallService.deleteVorlage(deleteId);
      showSuccess('Vorlage gelöscht');
      setDeleteId(null);
      await load();
    } catch {
      showError('Vorlage konnte nicht gelöscht werden.');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Button-Text und eingefügter Kommentar in Deutsch und Englisch. In der Bewertung und im Finalen Check gilt die
          Sprache des Audits; ist eine Sprache leer, wird die andere verwendet.
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNew} sx={{ flexShrink: 0 }}>
          Vorlage
        </Button>
      </Box>
      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Button DE / EN</TableCell>
              <TableCell>Text DE</TableCell>
              <TableCell>Text EN</TableCell>
              <TableCell>Reihenfolge</TableCell>
              <TableCell>Aktiv</TableCell>
              <TableCell align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && rows.map((row, index) => (
              <TableRow key={row.id} data-testid="firewall-vorlage-row">
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.button_label_de || <Leer />}</Typography>
                  <Typography variant="body2" color="text.secondary">{row.button_label_en || <Leer />}</Typography>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{row.text_de || <Leer />}</TableCell>
                <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{row.text_en || <Leer />}</TableCell>
                <TableCell>{row.sort_order}</TableCell>
                <TableCell>{row.aktiv ? 'Ja' : 'Nein'}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <IconButton aria-label="nach oben" disabled={index === 0} onClick={() => { void move(index, -1); }}>
                    <ArrowUpwardIcon fontSize="small" />
                  </IconButton>
                  <IconButton aria-label="nach unten" disabled={index === rows.length - 1} onClick={() => { void move(index, 1); }}>
                    <ArrowDownwardIcon fontSize="small" />
                  </IconButton>
                  <IconButton aria-label="bearbeiten" onClick={() => openEdit(row)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton aria-label="löschen" onClick={() => setDeleteId(row.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Vorlage bearbeiten' : 'Neue Vorlage'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', sm: 'row' }, mt: 0.5 }}>
            <LanguageColumn sprache="de" form={form} onChange={patch} />
            <LanguageColumn sprache="en" form={form} onChange={patch} />
          </Box>
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', mt: 1 }}>
            <TextField
              label="Reihenfolge"
              type="number"
              value={form.sort_order}
              onChange={(event) => patch({ sort_order: Number(event.target.value) })}
              margin="normal"
              sx={{ width: 160 }}
            />
            <FormControlLabel
              control={(
                <Checkbox
                  checked={form.aktiv}
                  onChange={(event) => patch({ aktiv: event.target.checked })}
                />
              )}
              label="Aktiv"
            />
          </Box>
          {(!hasLabel || !hasText) && (
            <Typography variant="caption" color="text.secondary">
              Button und Text müssen in mindestens einer Sprache gefüllt sein.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Abbrechen</Button>
          <Button variant="contained" onClick={() => { void save(); }} disabled={!hasLabel || !hasText}>
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={deleteId != null}
        title="Vorlage löschen"
        message="Die Vorlage wird aus der Bewertung entfernt."
        onConfirm={() => { void remove(); }}
        onCancel={() => setDeleteId(null)}
      />
    </Box>
  );
};

export default FirewallVorlagenPanel;
